import React, { useEffect } from 'react';
import { X, BookOpen, Lightbulb } from 'lucide-react';
import { useHelp } from '../../contexts/HelpContext';

interface NewGuideNotificationProps {
  notification: {
    id: string;
    guideId: string;
    title: string;
    message: string;
  };
  onDismiss: (id: string) => void;
}

export const NewGuideNotification: React.FC<NewGuideNotificationProps> = ({
  notification,
  onDismiss
}) => {
  const { openHelp } = useHelp();

  useEffect(() => {
    const timer = setTimeout(() => onDismiss(notification.id), 8000);
    return () => clearTimeout(timer);
  }, [notification.id, onDismiss]);

  const handleViewGuide = () => {
    openHelp(notification.guideId);
    onDismiss(notification.id);
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm bg-white dark:bg-dark-bg-secondary rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4 animate-in slide-in-from-right">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
            <Lightbulb className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary mb-1">
            {notification.title}
          </h4>
          <p className="text-sm text-gray-600 dark:text-dark-text-secondary mb-3">
            {notification.message}
          </p>

          <div className="flex items-center gap-2">
            <button
              onClick={handleViewGuide}
              className="inline-flex items-center gap-1 text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-md transition-colors"
            >
              <BookOpen className="w-4 h-4" />
              View Guide
            </button>

            <button
              onClick={() => onDismiss(notification.id)}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>

        <button
          onClick={() => onDismiss(notification.id)}
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

// Container component for multiple notifications
export const NewGuideNotifications: React.FC = () => {
  const { notifications, dismissNotification } = useHelp();

  if (notifications.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 space-y-2">
      {notifications.map((notification, index) => (
        <div
          key={notification.id}
          className="animate-in slide-in-from-right"
          style={{ animationDelay: `${index * 100}ms` }}
        >
          <NewGuideNotification
            notification={notification}
            onDismiss={dismissNotification}
          />
        </div>
      ))}
    </div>
  );
};